import React, { useCallback, useMemo } from 'react'
import moment from 'moment'
import CharterDurationToggleControl, { CharterDurationToggleOption } from 'components/OfferPage/Filters/CharterFlight/CharterDurationToggleControl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import { CHARTER_BUNDLE_DURATION_OPTIONS } from 'constants/charterFlight'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

interface Props {
  charterBundleRoute?: App.CharterBundleRoute
  duration: number
  durationOptions: Array<OfferDurationOption>
  checkInDate?: string
  rooms: Array<OfferPageRoom>
  priceMode: OfferPagePriceMode
  isMemberPrice: boolean
}

function CharterDurationToggle(props: Props) {
  const {
    charterBundleRoute,
    duration,
    durationOptions,
    checkInDate,
    rooms,
    priceMode,
    isMemberPrice,
  } = props

  const dispatch = useOfferPageDispatch()

  const [primaryDuration, secondaryDuration] = CHARTER_BUNDLE_DURATION_OPTIONS.map(option => option.value)
  const hasRequiredData = charterBundleRoute && primaryDuration && secondaryDuration

  const convertToDisplay = useMemo(() => getPriceDisplayFunction(priceMode, rooms), [priceMode, rooms])

  const getDurationPrice = useCallback((nights: number) => {
    const option = durationOptions.find(durationOption => durationOption.duration === nights)
    if (!option?.lowestPrice) {
      return undefined
    }
    return convertToDisplay(option.lowestPrice)
  }, [durationOptions, convertToDisplay])

  const onToggleChange = useCallback((parsedNights: number) => {
    if (!Number.isFinite(parsedNights) || parsedNights === duration) {
      return
    }

    if (checkInDate) {
      const newCheckOut = moment(checkInDate).add(parsedNights, 'days').format(ISO_DATE_FORMAT)
      dispatch({
        type: OfferPageActions.setDates,
        checkIn: checkInDate,
        checkOut: newCheckOut,
      })
      return
    }

    dispatch({ type: OfferPageActions.setDuration, duration: parsedNights })
  }, [checkInDate, dispatch, duration])

  const selectedValue = useMemo(() => {
    if (duration === primaryDuration || duration === secondaryDuration) {
      return duration
    }
    return primaryDuration ?? duration
  }, [duration, primaryDuration, secondaryDuration])

  const primaryPrice = getDurationPrice(primaryDuration)
  const secondaryPrice = getDurationPrice(secondaryDuration)

  const durationLabelLookup = useMemo(() => new Map(CHARTER_BUNDLE_DURATION_OPTIONS.map(option => [option.value, option.label])), [])

  const durationToggleOptions = useMemo<ReadonlyArray<CharterDurationToggleOption>>(() => {
    if (!primaryDuration || !secondaryDuration) {
      return []
    }
    return [{
      value: primaryDuration,
      label: durationLabelLookup.get(primaryDuration) ?? `${primaryDuration} nights + flights`,
      displayPrice: primaryPrice,
    }, {
      value: secondaryDuration,
      label: durationLabelLookup.get(secondaryDuration) ?? `${secondaryDuration} nights + flights`,
      displayPrice: secondaryPrice,
      disabled: secondaryPrice === undefined,
    }]
  }, [durationLabelLookup, primaryDuration, secondaryDuration, primaryPrice, secondaryPrice])

  if (!hasRequiredData || durationToggleOptions.length < 2) {
    return null
  }

  return (
    <CharterDurationToggleControl
      options={durationToggleOptions}
      selectedValue={selectedValue}
      unavailableMessage="Unavailable for your dates"
      onChange={onToggleChange}
      isMemberPrice={isMemberPrice}
    />
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  charterBundleRoute: state.charterBundleRoute,
  duration: state.duration,
  checkInDate: state.checkInDate,
  durationOptions: getDurationOptions(state, appState),
  rooms: state.rooms,
  priceMode: state.priceMode,
  isMemberPrice: state.displayMemberPrice,
}), CharterDurationToggle)
